import {
  GoAButton,
  GoAButtonGroup,
  GoAFormItem,
  GoAIconButton,
  GoAModal,
  GoATextArea,
} from "@abgov/react-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Notification from "../../components/Notification";
import { Order, OrderItem } from "../../model/Order";
import { useRecoilState } from "recoil";
import { orderPdfState, orderState } from "../../states/Order.state";
import { OrderService } from "../../services/OrderService";
import {
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_COMPLETE,
  ORDER_STATUS_DRAFT,
  ORDER_STATUS_ORDERED,
  orderActionStatusList,
  orderTypeOptions
} from "../../common/constants";
import { Typography, useMediaQuery } from "@mui/material";
import NumericInput from "../../components/NumericInput";
import { getUserEmail, getUserName } from "../../utils/tokens";
import { useAuth } from "react-oidc-context";
import { loadCommonLookups, notificationMessageState, showNotificationState } from "../../states/Common.state";
import { INotificationMessage } from "../../model/Common";
import { OrderPdf } from "../../model/OrderPdf";
import { getUtcDate } from "../../common/date";
import dayjs from "dayjs";
import moment from "moment";
import PageLoader from "../../components/PageLoader";
import OrderComments from "./OrderComments";
import { UserComment } from "../../model/Base";
import DomainItemAutocomplete from "../../components/DomainItemPicker";
import CrewTypePicker from "../../components/CrewTypePicker";
import { getDataFromSessionStorage } from "../../utils/SessionStorageUtils";
import { ForObjectType } from "../../model/enums/Constants";
import { DomainLookupType } from "../../services/DomainService";
import React from "react";

interface IOrderDetailProps {
  orderData: Order;
  redirectedFrom?: string;
}

const OrderViewUpdateByRequester: React.FC<IOrderDetailProps> = (props) => {
  //const itemRefs = useRef<(HTMLInputElement | null)[]>([]);
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width: 480px)');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderData, setOrderData] = useRecoilState<Order>(orderState);
  const [formStateChanged, setFormStateChanged] = useState<boolean>(false);
  const [showCancelOrderWarning, setShowCancelOrderWarning] =
    useState<boolean>(false);
  const [showDraftWarning, setShowDraftWarning] = useState<boolean>(false);
  const [orderPdf, setOrderPdf] = useRecoilState<OrderPdf>(orderPdfState);

  const [showNotification, setShowNotification] = useRecoilState<boolean>(showNotificationState);
  const [notificationMessage, setNotificationMessage] = useRecoilState<INotificationMessage>(notificationMessageState);
  const [showDiscardChangesWarning, setShowDiscardChangesWarning] = useState<boolean>(false);
  const [reasonForCancellation, setReasonForCancellation] = useState<string>("");
  const [orderTypeId, setOrderTypeId] = useState<string>("");

  const isPersonnelOrderType = orderData.orderType === "Personnel";

  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (focusIndex !== null) {
      const input = inputRefs.current[focusIndex];
      if (input) {
        input.focus();
        setFocusIndex(null);
      }
    }
  }, [orderData.orderItems, focusIndex]);

  const handleSubmitOrder = async () => {
    if (!validateForm('save')) {
      return;
    }

    setIsLoading(true);
    const updatedOrderData = { ...orderData, status: ORDER_STATUS_ORDERED, submittedDate: getUtcDate(new Date()) };

    setOrderData(updatedOrderData);
    const result = await OrderService.update(updatedOrderData);
    if (result.success) {
      const updatedOrder = await OrderService.getByOrderNumber(orderData.orderNumber);
      setOrderData(updatedOrder.data);
      //set pdf data - start
      const pdfData = { ...updatedOrder.data };
      pdfData.statusDesc = orderActionStatusList.find(i => i.value === pdfData.status)?.label ?? "";
      pdfData.printDateTime = moment(new Date()).format('YYYY-MM-DD HH:mm');
      pdfData.orderDatePrintable = dayjs.utc(pdfData.orderDate).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm");
      pdfData.submittedDatePrintable = dayjs.utc(pdfData.submittedDate).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm");
      pdfData.requiredByPrintable = dayjs.utc(pdfData.requiredBy).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm");
      setOrderPdf({ ...orderPdf, data: pdfData });
      //set pdf data - end
      setIsLoading(false);
      setShowNotification(true);
      setNotificationMessage({ messageType: 'success', messageHeader: 'Order successfully submitted.', messageBody: window.location.origin + `/order/orderNumber/${updatedOrderData.orderNumber}` });
      setFormStateChanged(false);
    } else {
      setIsLoading(false);
      setShowNotification(true);
      setNotificationMessage({ messageType: 'error', messageHeader: 'Error submitting order.' });
    }
    console.log(formStateChanged);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleSaveDraft = async () => {
    if (orderData.status === ORDER_STATUS_ORDERED && !validateForm('save')) {
      return;
    }
    // Deep clone and sanitize orderItems
    const updatedOrderData = {
      ...orderData,
      orderItems: orderData?.orderItems?.map(item => ({
        ...item,
        quantityFulfilled: item.quantityFulfilled ?? undefined
      })) ?? []
    };
    // Check for duplicate item names
    if (CheckDuplicateItemExists()) return;


    setIsLoading(true);
    const result = await OrderService.update(updatedOrderData);

    if (result.success) {
      debugger;
      const updatedOrder = await OrderService.getByOrderNumber(orderData.orderNumber);
      setOrderData(updatedOrder.data);
      //set pdf data - start
      const pdfData = { ...updatedOrder.data };
      pdfData.statusDesc = orderActionStatusList.find(i => i.value === pdfData.status)?.label ?? "";
      pdfData.printDateTime = moment(new Date()).format('YYYY-MM-DD HH:mm');
      pdfData.orderDatePrintable = dayjs.utc(pdfData.orderDate).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm");
      pdfData.submittedDatePrintable = dayjs.utc(pdfData.submittedDate).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm");

      pdfData.requiredByPrintable = dayjs.utc(pdfData.requiredBy).tz("America/Edmonton", false).format("YYYY-MM-DD HH:mm");
      pdfData.isPersonnelOrderType = isPersonnelOrderType;
      setOrderPdf({ ...orderPdf, data: pdfData });
      //set pdf data - end
      setIsLoading(false);
      setShowNotification(true);
      setNotificationMessage({ messageType: 'success', messageHeader: 'Order saved successfully.' });
      setFormStateChanged(false);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } else {
      setIsLoading(false);
      setShowNotification(true);
      setNotificationMessage({ messageType: 'error', messageHeader: 'Error saving order.' });
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }
  };

  const handleCancelOrder = async () => {
    setErrors({});
    setShowCancelOrderWarning(false);
    setReasonForCancellation("");
    setIsLoading(true);

    let updatedSuccess = false;

    // Create cancellation comment
    const cancellationComment: UserComment = {
      id: undefined,
      text: `Order cancelled (Reason: ${reasonForCancellation})`,
      username: auth.user?.profile.name ?? "",
      postDate: new Date().toISOString(),
      postDateString: `${dayjs().tz("America/Edmonton", false).format("dddd, MMMM D, YYYY [at] HH:mm")} (MST)`,
    };

    const updatedOrderData = { ...orderData };
    updatedOrderData.comments = [...(updatedOrderData.comments || []), cancellationComment]?.sort((a: UserComment, b: UserComment) => new Date(b.postDate).getTime() - new Date(a.postDate).getTime());
    updatedOrderData.status = ORDER_STATUS_CANCELLED;

    setOrderData(updatedOrderData);

    try {
      const updateResult = await OrderService.update(updatedOrderData);

      if (updateResult.success) {
        const commentResult = await OrderService.addComment(updatedOrderData);

        if (commentResult.success) {
          updatedSuccess = true;

          const pdfData = {
            ...updatedOrderData,
            statusDesc:
              orderActionStatusList.find((i) => i.value === updatedOrderData.status)?.label ?? "",
          };

          setOrderPdf({ ...orderPdf, data: pdfData });

          setNotificationMessage({
            messageType: "success",
            messageHeader: "Order successfully cancelled.",
          });
        }
      }
    } catch (error) {
      console.error("Cancellation error:", error);
    }

    if (!updatedSuccess) {
      setNotificationMessage({
        messageType: "error",
        messageHeader: "Error cancelling order.",
      });
    }

    setIsLoading(false);
    setShowNotification(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleAddOrderItem = useCallback((orderType: string) => {
    const orderItems = [...(orderData?.orderItems ?? [])];
    orderItems.push(new OrderItem({
      orderId: orderData?.id,
      itemType: orderType,
      isCrewTypeItem: orderType === "CrewType"
    }));
    setOrderData({ ...orderData, orderItems });
    setFormStateChanged(true);
    setFocusIndex(orderItems.length - 1);
    ; // Track the new item's index
  }, [orderData, setOrderData]);

  const handleRemoveOrderItem = (id: string | undefined, index: number) => {
    const orderItems = [...(orderData?.orderItems ?? [])];

    if (!id) {
      orderItems.splice(index, 1);
    } else {
      orderItems[index] = { ...orderItems[index], isActive: null };
    }

    setOrderData({ ...orderData, orderItems });
    setFormStateChanged(true);

    if (orderItems.length > 0) {
      setFocusIndex(orderItems.length - 1);
    }

  };

  const handleItemNameChange = (name: string, val: string, index: number) => {
    setShowNotification(false);
    setErrors({});
    const orderItems = [...(orderData?.orderItems ?? [])];
    if (val !== orderData.orderItems[index].itemName) {
      setFormStateChanged(true);
      orderItems[index] = { ...orderItems[index], itemType: name, itemName: val };
      setOrderData({ ...orderData, orderItems });
    }
  };

  const handleQuantityChange = (val: number, index: number) => {
    setShowNotification(false);
    setErrors({});
    const orderItems = [...(orderData?.orderItems ?? [])];
    if (val !== orderData.orderItems[index].quantityOrdered) {
      setFormStateChanged(true);
      orderItems[index] = { ...orderItems[index], quantityOrdered: val };
      setOrderData({ ...orderData, orderItems });
    }
  };

  function validateForm(name: string = "", value: string = "") {
    setErrors({});
    const newErrors: Record<string, string> = {};
    if (orderData?.orderItems) {
      orderData.orderItems.forEach((item, index) => {
        if (((item.id && item.isActive) || !item.id) && !item.itemName) {
          newErrors['order-itemname-' + index] = "Item name is required.";
        }
        if (((item.id && item.isActive && item.isCrewTypeItem) || !item.id) && !item.itemName) {
          newErrors['order-itemname-crewtype-' + index] = "Item name is required.";
        }
        if (((item.id && item.isActive) || !item.id) && (!item.quantityOrdered || (item.quantityOrdered && item.quantityOrdered < 1))) {
          newErrors['order-quantity-' + index] = "Quantity is required.";
        }
        if (((item.id && item.isActive && item.isCrewTypeItem) || !item.id) && (!item.quantityOrdered || (item.quantityOrdered && item.quantityOrdered < 1))) {
          newErrors['order-quantity-crewtype-' + index] = "Quantity is required.";
        }
      });
    }

    if (!orderData?.orderItems.some(item => (item.id && item.isActive) || !item.id)) {
      newErrors['order-item'] = "Please add at least one item to this order before submit.";
    }

 
    

    const itemNames = orderData?.orderItems
      .filter(item => (item.id && item.isActive) || !item.id)
      .map(item => item.itemName?.trim().toLowerCase())
      .filter(name => !!name); // remove empty/null names

    const hasDuplicateNames = new Set(itemNames).size !== itemNames.length;

    if (hasDuplicateNames) {
      newErrors["order-item"] =
        "Item names must be unique.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if there are no errors
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNotification(false);
    }, 30000);

    // Cleanup timer on unmount
    return () => clearTimeout(timer);
  }, [showNotification, setShowNotification]);

  useEffect(() => {
    const fetchData = async () => {
      let lookupTypes = getDataFromSessionStorage(ForObjectType.DOMAIN_LOOKUP_TYPES);

      if (!lookupTypes) {
        await loadCommonLookups();
        lookupTypes = getDataFromSessionStorage(ForObjectType.DOMAIN_LOOKUP_TYPES);
      }

      const orderTypelookupName = orderTypeOptions.find(opt => opt.value === orderData.orderType)?.name ?? "";

      const orderTypeId = lookupTypes
        ?.find((x: DomainLookupType) => x.name === orderTypelookupName)
        ?.id;
      setOrderTypeId(orderTypeId);
    };

    fetchData();

  },);

  function CheckDuplicateItemExists() {
    const newErrors: Record<string, string> = {};
    const itemNames = orderData?.orderItems
      .filter(item => (item.id && item.isActive) || !item.id)
      .map(item => item.itemName?.trim().toLowerCase())
      .filter(name => !!name); // remove empty/null names

    const hasDuplicateNames = new Set(itemNames).size !== itemNames.length;

    if (hasDuplicateNames) {
      newErrors["order-item"] =
        "Item names must be unique.";
    }

    setErrors(newErrors);

    return hasDuplicateNames;
  }

  const isMyOrder = (props.orderData.auditTrail?.createdBy === getUserEmail(auth?.user?.access_token ?? "") || props.orderData.orderingForName?.includes(getUserName(auth?.user?.access_token ?? "")));
  const isEditMode = isMyOrder && (orderData.status === ORDER_STATUS_DRAFT || orderData.status === ORDER_STATUS_ORDERED); //User can edit only draft and ordered orders and also if the order is created by him/her.
  const isOrderNotCancelledOrCompleted = (orderData.status !== ORDER_STATUS_CANCELLED && orderData.status !== ORDER_STATUS_COMPLETE);


  return (
    <>
      <PageLoader visible={loading} />
      <div className="ordered-items-container-new">
        {(orderData.orderType === "Personnel" || orderData.orderItems.some(x => x.isCrewTypeItem)) && <> <table className={"order-add-items-container"}><thead>
          <tr>
            <th className="col1"><Typography color="textPrimary" variant="h5">Crews</Typography>
            </th>
          </tr>

          <tr>

            <th className="col1">Items</th>
            <th className="col2">Quantity</th>
            {orderData.status !== ORDER_STATUS_DRAFT &&
              orderData.orderItems?.some(
                (i) =>
                  i.quantityFulfilled !== undefined &&
                  i.quantityFulfilled !== null &&
                  i.quantityFulfilled >= 0
              ) && <th className="col3">Quantity fullfilled</th>}
            {orderData.status === ORDER_STATUS_DRAFT && (
              <th className="col4"></th>
            )}
          </tr>
        </thead>
          <tbody>
            <tr>
              <td>
                <GoAFormItem error={errors["order-item"]}></GoAFormItem>
              </td>
            </tr>

            {orderData.orderItems?.map((item, index) => {
              return (
                item.isCrewTypeItem && (!item.id || item.isActive) && (
                  <tr key={index}>
                    {isEditMode ? (
                      <td>
                        <GoAFormItem error={errors["order-itemname-crewtype-" + index]}>
                          <CrewTypePicker
                            ref={(el) => (inputRefs.current[index] = el)}
                            item={item}
                            selectedItems={orderData.orderItems}
                            index={index}
                            handleItemNameChange={(value: string, index: number) => handleItemNameChange("CrewType", value, index)}
                          />
                        </GoAFormItem>{" "}
                      </td>
                    ) : (
                      <td className="order-item-td-readonly">
                        <span>{item.itemName}</span>
                      </td>
                    )}

                    {isEditMode ? (
                      <td>
                        <GoAFormItem error={errors["order-quantity-crewtype-" + index]}>
                          <NumericInput
                            width={isMobile ? 75 : 100}
                            handleQuantityChange={handleQuantityChange}
                            value={item.quantityOrdered || 0}
                            index={index}
                            verticalAlign="initial"
                          />
                        </GoAFormItem>{" "}
                      </td>
                    ) : (
                      <td className="order-item-td-readonly">
                        <span>{item.quantityOrdered || 0}</span>
                      </td>
                    )}

                    {orderData.status !== ORDER_STATUS_DRAFT &&
                      item.quantityFulfilled !== undefined &&
                      item.quantityFulfilled !== null &&
                      item.quantityFulfilled >= 0 && (
                        <td className="order-item-td-readonly">
                          {item.quantityFulfilled || undefined}
                        </td>
                      )}
                    {isEditMode && (
                       <td className="item-remove-button">
                        <GoAIconButton
                          icon="trash"
                          variant="destructive"
                          onClick={() => {
                            setShowNotification(false);
                            handleRemoveOrderItem(item.id, index);
                          }}
                        ></GoAIconButton>
                      </td>
                    )}
                  </tr>
                )
              );
            })}
          </tbody>
        </table>
          {isEditMode && (
            <div>
              <GoAButton
                type="secondary"
                onClick={() => {
                  setShowNotification(false);
                  handleAddOrderItem('CrewType');
                }}
              >
                Add item +
              </GoAButton>
            </div>
          )}</>}

        <table className={"order-add-items-container"}>
          <thead>
            <tr>
              <th className="col1">{orderData.orderType === "Personnel" && <Typography color="textPrimary" variant="h5">Individual positions</Typography>}
              </th>
            </tr>
            <tr>
              <th className="col1">{`${orderData.orderType}`}</th>
              <th className="col2">Quantity</th>
              {orderData.status !== ORDER_STATUS_DRAFT &&
                orderData.orderItems?.some(
                  (i) =>
                    i.quantityFulfilled !== undefined &&
                    i.quantityFulfilled !== null &&
                    i.quantityFulfilled >= 0
                ) && <th className="col3">Quantity fullfilled</th>}
              {orderData.status === ORDER_STATUS_DRAFT && (
                <th className="col4"></th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <GoAFormItem error={errors["order-item"]}></GoAFormItem>
              </td>
            </tr>

            {orderData.orderItems?.map((item, index) => {
              return (
                ((item.id && item.isActive && (!item.itemType || !item.isCrewTypeItem)) || (!item.id && !item.isCrewTypeItem)) && (
                  <tr key={index}>
                    {isEditMode ? (
                      <td>
                        <GoAFormItem error={errors["order-itemname-" + index]}>
                          <DomainItemAutocomplete
                            ref={(el) => (inputRefs.current[index] = el)}
                            item={item}
                            selectedItems={orderData.orderItems}
                            lookupTypeId={orderTypeId}
                            index={index}
                            handleItemNameChange={(value: string, index: number) => handleItemNameChange(orderData.orderType, value, index)}
                          />
                        </GoAFormItem>{" "}
                      </td>
                    ) : (
                      <td className="order-item-td-readonly">
                        <span>{item.itemName}</span>
                      </td>
                    )}

                    {isEditMode ? (
                      <td>
                        <GoAFormItem error={errors["order-quantity-" + index]}>
                          <NumericInput
                            width={isMobile ? 75 : 100}
                            handleQuantityChange={handleQuantityChange}
                            value={item.quantityOrdered || 0}
                            index={index}
                            verticalAlign="initial"
                          />
                        </GoAFormItem>{" "}
                      </td>
                    ) : (
                      <td className="order-item-td-readonly">
                        <span>{item.quantityOrdered || 0}</span>
                      </td>
                    )}

                    {orderData.status !== ORDER_STATUS_DRAFT &&
                      item.quantityFulfilled !== undefined &&
                      item.quantityFulfilled !== null &&
                      item.quantityFulfilled >= 0 && (
                        <td className="order-item-td-readonly">
                          {item.quantityFulfilled || undefined}
                        </td>
                      )}
                    {isEditMode && (
                      <td className="remove-item-button">
                        <GoAIconButton
                          icon="trash"
                          variant="destructive"
                          onClick={() => {
                            setShowNotification(false);
                            handleRemoveOrderItem(item.id, index);
                          }}
                        ></GoAIconButton>
                      </td>
                    )}
                  </tr>
                )
              );
            })}
          </tbody>
        </table>
        {isEditMode && (
          <div>
            <GoAButton
              type="secondary"
              onClick={() => {
                setShowNotification(false);
                handleAddOrderItem(orderData.orderType);
              }}
            >
              Add item +
            </GoAButton>
          </div>
        )}

        <br />

        <div className="comments-container">
          <OrderComments />
        </div>

        <div
          className={`${isMobile
            ? "update-order-button-group-requester-mobile"
            : "update-order-button-group-requester"
            }`}
        >
          {isEditMode && orderData.status === ORDER_STATUS_DRAFT && (
            <div className="submit-order-button">
              <GoAButton type="primary" onClick={handleSubmitOrder}>
                Submit order
              </GoAButton>
            </div>
          )}
          {(isEditMode || (!isEditMode && isOrderNotCancelledOrCompleted)) && (
            <div
              className={`save-button ${orderData.status !== ORDER_STATUS_DRAFT ? "ordered-status" : ""
                }`}
            >
              <GoAButton
                type="secondary"
                disabled={!formStateChanged}
                onClick={() => {
                  handleSaveDraft();
                }}
              >
                Save
              </GoAButton>
            </div>
          )}

          <div
            className={`back-to-listview-button ${(isEditMode || (!isEditMode && isOrderNotCancelledOrCompleted)) &&
              (orderData.status !== ORDER_STATUS_DRAFT
                ? "ordered-status"
                : "draft-status")
              }`}
          >
            <GoAButton
              type="secondary"
              onClick={() =>
                formStateChanged
                  ? setShowDiscardChangesWarning(true)
                  : navigate("/ordersList")
              }
            >
              Back to list view
            </GoAButton>
          </div>

          {isMyOrder &&
            (props.orderData.status === ORDER_STATUS_DRAFT ||
              props.orderData.status === ORDER_STATUS_ORDERED) && (
              <div
                className={`cancel-order-button ${orderData.status === ORDER_STATUS_DRAFT
                  ? "draft-status"
                  : orderData.status === ORDER_STATUS_ORDERED
                    ? "ordered-status"
                    : ""
                  }`}
              >
                <GoAButton
                  type="primary"
                  variant="destructive"
                  onClick={() => setShowCancelOrderWarning(true)}
                >
                  Cancel order
                </GoAButton>
              </div>
            )}
        </div>
        {showNotification && (
          <Notification
            messageType={notificationMessage.messageType}
            messageBody={notificationMessage.messageBody}
            messageHeader={notificationMessage.messageHeader}
          ></Notification>
        )}
      </div>






      <GoAModal heading="Cancel Order" open={showCancelOrderWarning}>
        <GoAFormItem label="Reason">
          <GoATextArea
            name="ReasonForCancellation"
            ariaLabel="Reason For Cancellation"
            countBy="character"
            maxCount={500}
            onChange={(name, value) => { setReasonForCancellation(value) }}
            value={reasonForCancellation}
          ></GoATextArea>
        </GoAFormItem>
        <div className="cancel-order-warning-button-group">
          <GoAButtonGroup alignment="end">
            <GoAButton
              type="secondary"
              onClick={() => { setReasonForCancellation(""); setShowCancelOrderWarning(false); }}
            >
              Close
            </GoAButton>
            <GoAButton type="primary" variant="destructive" onClick={handleCancelOrder} disabled={!reasonForCancellation || reasonForCancellation.length > 500}>
              Cancel Order
            </GoAButton>
          </GoAButtonGroup>
        </div>
      </GoAModal>
      <GoAModal
        heading="Do you want to save this as a draft and return to the Orders list view?"
        open={showDraftWarning}
      >
        <GoAButtonGroup alignment="end">
          <GoAButton
            type="secondary"
            onClick={() => setShowDraftWarning(false)}
          >
            Cancel
          </GoAButton>
          <GoAButton type="primary" onClick={handleSaveDraft}>
            Save
          </GoAButton>
        </GoAButtonGroup>
      </GoAModal>
      <GoAModal
        heading="Are you sure you want to discard the changes?"
        open={showDiscardChangesWarning}
      >
        <GoAButtonGroup alignment="end">
          <GoAButton
            type="secondary"
            onClick={() => setShowDiscardChangesWarning(false)}
          >
            Cancel
          </GoAButton>
          <GoAButton type="primary" onClick={() => navigate("/ordersList")}>
            Confirm
          </GoAButton>
        </GoAButtonGroup>
      </GoAModal>
    </>
  );
};

export default OrderViewUpdateByRequester;


